/**
 * apercuDePlan — le seul lecteur de ce que rend le moteur de plans : l'aperçu
 * de `doc.mutations.preview`, et le reçu de `doc.mutations.apply`.
 *
 * WHY un module à part : quatre verbes passent par le moteur de plans
 * (`apply_edit_plan`, `format_text`, `format_paragraphs`, `replace_text` en mode
 * partout) et décident sur ces réponses. Un lecteur par verbe finirait par
 * accepter deux formes différentes de la même réponse du fournisseur.
 *
 * Forme relevée sur SuperDoc 2.15.1 (`mesure-format-text-cibles`, 2026-10-02) :
 * `{evaluatedRevision, steps:[{stepId, op, matchCount?, …}], valid, failures?:
 * [{code, stepId, phase, message}]}`. Une étape qui vise du texte porte aussi
 * `resolutions:[{target:{kind:'text', blockId, range}, range:{from, to}, text}]`,
 * une par plage résolue, y compris au-delà de cent étapes
 * (`mesure-format-text-only-where`, Q1 et Q9, 2026-10-02).
 */

/** Vrai pour tout objet non nul : la seule garde avant de lire un champ d'une réponse du fournisseur. */
export function estObjet(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/** L'aperçu d'un plan, réduit aux champs sur lesquels un exécuteur décide. */
export interface ProviderPreview {
  evaluatedRevision: string;
  valid: boolean;
  steps: Array<{ stepId: string; op: string; matchCount?: number }>;
  /** Les refus, étape par étape ; `message` est la cause que l'éditeur écrit en clair. */
  failures?: Array<{ stepId: string; code: string; message?: string }>;
}

/**
 * Lit l'aperçu rendu par le fournisseur.
 *
 * @returns L'aperçu, ou `null` si la réponse n'a pas la forme relevée : un
 *   aperçu illisible ne vaut ni accord ni refus, l'appelant n'écrit pas.
 */
export function lirePreview(value: unknown): ProviderPreview | null {
  if (!estObjet(value) || typeof value.evaluatedRevision !== 'string' || typeof value.valid !== 'boolean' || !Array.isArray(value.steps)) return null;
  const steps = value.steps.flatMap((step) => (
    estObjet(step) && typeof step.stepId === 'string' && typeof step.op === 'string'
      ? [{
          stepId: step.stepId,
          op: step.op,
          ...(typeof step.matchCount === 'number' && Number.isInteger(step.matchCount) ? { matchCount: step.matchCount } : {}),
        }]
      : []
  ));
  if (steps.length !== value.steps.length) return null;
  const failures = Array.isArray(value.failures)
    ? value.failures.flatMap((failure) => (
      estObjet(failure) && typeof failure.stepId === 'string' && typeof failure.code === 'string'
        ? [{ stepId: failure.stepId, code: failure.code, ...(typeof failure.message === 'string' ? { message: failure.message } : {}) }]
        : []
    ))
    : undefined;
  if (Array.isArray(value.failures) && failures?.length !== value.failures.length) return null;
  return { evaluatedRevision: value.evaluatedRevision, valid: value.valid, steps, ...(failures === undefined ? {} : { failures }) };
}

/** Ce que l'éditeur vise pour une étape : le paragraphe, la plage dans son texte, et le texte qu'il y lit. */
export interface ResolutionDEtape {
  blockId: string;
  debut: number;
  /** Exclue. */
  fin: number;
  texte: string;
}

/**
 * Les plages que l'aperçu a résolues, par identifiant d'étape.
 *
 * WHY à part de {@link lirePreview} : c'est la PREUVE qu'une étape adressée par
 * position vise le texte attendu (`format_text` avec `onlyWhere`). Une étape
 * dont une résolution n'a pas la forme relevée n'a pas d'entrée : l'appelant la
 * tient pour non prouvée, jamais pour juste.
 */
export function resolutionsParEtape(value: unknown): Map<string, ResolutionDEtape[]> {
  const parEtape = new Map<string, ResolutionDEtape[]>();
  if (!estObjet(value) || !Array.isArray(value.steps)) return parEtape;
  for (const step of value.steps) {
    if (!estObjet(step) || typeof step.stepId !== 'string' || !Array.isArray(step.resolutions)) continue;
    const resolutions = step.resolutions.flatMap((resolution): ResolutionDEtape[] => {
      if (!estObjet(resolution) || !estObjet(resolution.target) || !estObjet(resolution.range)) return [];
      const { blockId } = resolution.target;
      const { from, to } = resolution.range;
      return typeof blockId === 'string' && typeof from === 'number' && typeof to === 'number' && Number.isInteger(from) && Number.isInteger(to)
        && typeof resolution.text === 'string'
        ? [{ blockId, debut: from, fin: to, texte: resolution.text }]
        : [];
    });
    if (resolutions.length === step.resolutions.length) parEtape.set(step.stepId, resolutions);
  }
  return parEtape;
}

/** L'effet qu'un plan appliqué annonce pour une étape. */
export type EffetDEtapeDePlan = 'changed' | 'noop' | 'error' | 'assert_passed' | 'assert_failed';

const EFFETS_D_ETAPE: readonly EffetDEtapeDePlan[] = ['changed', 'noop', 'error', 'assert_passed', 'assert_failed'];

function estEffetDePlan(value: unknown): value is EffetDEtapeDePlan {
  return typeof value === 'string' && EFFETS_D_ETAPE.some((effet) => effet === value);
}

/** Le reçu d'un plan appliqué, réduit aux champs sur lesquels un exécuteur prouve son écriture. */
export interface RecuDePlan {
  success: true;
  revision: { before: string; after: string };
  steps: Array<{
    stepId: string;
    op: string;
    effect: EffetDEtapeDePlan;
    matchCount: number;
    trackedChangeIds?: string[];
  }>;
}

/**
 * Lit le reçu que rend `doc.mutations.apply` pour un plan accepté.
 *
 * @returns Le reçu, ou `null` si la réponse n'a pas cette forme en entier —
 *   une étape illisible comprise : un reçu illisible ne prouve aucune étape,
 *   l'appelant ne peut rien attribuer à son plan.
 */
export function lireRecuDePlan(value: unknown): RecuDePlan | null {
  if (!estObjet(value) || value.success !== true || !estObjet(value.revision)
    || typeof value.revision.before !== 'string' || typeof value.revision.after !== 'string' || !Array.isArray(value.steps)) return null;
  const steps = value.steps.flatMap((step): RecuDePlan['steps'] => {
    if (!estObjet(step) || typeof step.stepId !== 'string' || typeof step.op !== 'string'
      || !estEffetDePlan(step.effect) || typeof step.matchCount !== 'number' || !Number.isInteger(step.matchCount) || step.matchCount < 0) return [];
    const lus: unknown = step.trackedChangeIds;
    const trackedChangeIds = Array.isArray(lus) && lus.every((id): id is string => typeof id === 'string' && id.length > 0) ? lus : undefined;
    return [{
      stepId: step.stepId,
      op: step.op,
      effect: step.effect,
      matchCount: step.matchCount,
      ...(trackedChangeIds === undefined ? {} : { trackedChangeIds }),
    }];
  });
  return steps.length === value.steps.length ? { success: true, revision: { before: value.revision.before, after: value.revision.after }, steps } : null;
}
